/* =====================================================================
   onPremises.ts — the systems that stay on-site, and the links back to them.

   The architecture diagram is derived by the model from the Terraform, and the Terraform builds
   only the cloud: everything VaultSentinel placed on-site (IBM Z, Solaris / HP-UX, telephony
   equipment, OT - backend/src/lib/iac-placement.js) was simply missing, with nothing to say the
   cloud depends on it. Andrew, 22 Oct 2026: "The architecture and the detailed views need to show
   links back to on premise".

   This adds them from the server's placement, not from the model:
   - one area, "On-premises — isolated recovery network", grouped by platform;
   - a site link from the cloud's VPN / private-link gateway (when the Terraform has one) to it;
   - in the Detailed view, a link from each on-site system that a cloud system depends on, or that
     depends on one (the CROSSINGS), labelled with what it serves.

   The engineer's answers move things: an off-cloud object whose id appears in the Terraform (every
   resource is tagged SourceObjectID) was built in the cloud - Power Virtual Server, or call
   control moved to the cloud - and is not drawn on-site.
   ===================================================================== */

import type { Architecture, Container, Edge, Node } from "./types";
import type { IaCContext, IaCContextObject } from "../../api";

export const ON_PREM_ROOT = "onprem:root";

const GROUPS: Array<{ id: string; label: string; match: (o: IaCContextObject) => boolean }> = [
  { id: "ibm-z", label: "IBM Z mainframe", match: (o) => o.placement.group === "ibm-z" },
  { id: "ibm-power", label: "IBM Power (AIX / IBM i)", match: (o) => o.placement.group === "ibm-power" },
  { id: "midrange-unix", label: "Solaris / HP-UX", match: (o) => o.placement.group === "midrange-unix" },
  { id: "telephony", label: "Telephony", match: (o) => o.placement.group === "telephony" || o.placement.group === "telephony-site" },
  { id: "ot", label: "Operational technology", match: (o) => o.placement.group === "ot" },
  { id: "site-infrastructure", label: "Site infrastructure", match: (o) => !!o.placement.alsoAtSite?.length },
  { id: "other", label: "Other on-site systems", match: () => true },
];

/** Terraform resource types that ARE the link to a site. */
const SITE_LINK_TYPES = /^(aws_vpn_connection|aws_vpn_gateway|aws_customer_gateway|aws_ec2_transit_gateway|aws_dx_|azurerm_virtual_network_gateway|azurerm_local_network_gateway|azurerm_express_route|google_compute_(ha_)?vpn_|google_compute_external_vpn_gateway|google_compute_interconnect|ibm_is_vpn_gateway|ibm_dl_|ibm_tg_gateway)/;

/** Whether the Terraform names this object (its SourceObjectID tag, or anywhere else). */
function namedIn(terraform: string, objectId: string): boolean {
  const escaped = objectId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`["'=\\s]${escaped}["'\\s,]`).test(terraform);
}

/** The objects drawn on-site: placed off-cloud (or needed at the site too), and not built in the cloud. */
export function onSiteObjects(ctx: IaCContext, terraform: string): IaCContextObject[] {
  return ctx.hierarchyData.filter((o) => {
    const p = o.placement;
    const offCloud = p?.placement === "recovery-site" || p?.placement === "ibm-power";
    if (p?.alsoAtSite?.length) return true;
    return offCloud && !namedIn(terraform, o.objectId);
  });
}

const short = (o: IaCContextObject | undefined, id: string) => (o?.description || id).replace(/\s+/g, " ").slice(0, 28);

/**
 * The architecture with the on-site area and its links added. Unchanged when nothing is on-site.
 * Never mutates its input.
 */
export function withOnPremises(arch: Architecture, ctx: IaCContext | null | undefined, terraform: string): Architecture {
  if (!ctx?.hierarchyData?.length) return arch;
  const onSite = onSiteObjects(ctx, terraform);
  if (onSite.length === 0) return arch;

  const sites = [...new Set(onSite.map((o) => o.locationName).filter(Boolean))] as string[];
  const containers: Container[] = [{
    id: ON_PREM_ROOT,
    label: "On-premises — isolated recovery network",
    kind: "on-premises",
    sublabel: sites.join(" · ") || undefined,
  }];
  const nodes: Node[] = [];
  const placedIn = new Map<string, string>();
  for (const o of onSite) {
    const group = GROUPS.find((g) => g.match(o))!;
    const containerId = `onprem:${group.id}`;
    if (!containers.some((c) => c.id === containerId)) {
      containers.push({ id: containerId, label: group.label, kind: "on-premises-group", parentId: ON_PREM_ROOT });
    }
    const nodeId = `onprem:obj:${o.objectId}`;
    placedIn.set(o.objectId, nodeId);
    nodes.push({
      id: nodeId,
      label: o.objectId,
      service: [o.description, o.placement.platform, o.placement.reason].filter(Boolean).join(" — "),
      category: group.id === "telephony" ? "network" : o.isDatastore ? "database" : "compute",
      containerId,
      sublabel: o.description ? o.description.slice(0, 40) : undefined,
      onPremises: true,
    });
  }

  // The cloud end of the link: the VPN / private-link gateway the Terraform builds, else the
  // outermost cloud container (the link is then still to be generated, and says so).
  const gateway = arch.nodes.find((n) => n.resourceType && SITE_LINK_TYPES.test(n.resourceType));
  const cloudRoot = arch.containers.find((c) => !c.parentId);
  const cloudEnd = gateway?.id ?? cloudRoot?.id;
  const edges: Edge[] = [];
  if (cloudEnd) {
    edges.push({ source: cloudEnd, target: ON_PREM_ROOT, kind: "site-link", label: gateway ? "Site link" : "Site link (not in the Terraform yet)" });
  }

  // Each on-site system with a cloud dependency: one link to the gateway, labelled with what it serves.
  const byId = new Map(ctx.hierarchyData.map((o) => [o.objectId, o]));
  const across = new Map<string, Set<string>>();
  for (const c of ctx.placement?.crossings ?? []) {
    const site = placedIn.has(c.provider) ? c.provider : placedIn.has(c.dependent) ? c.dependent : null;
    if (!site) continue;
    const other = site === c.provider ? c.dependent : c.provider;
    if (placedIn.has(other)) continue;
    if (!across.has(site)) across.set(site, new Set());
    across.get(site)!.add(other);
  }
  if (cloudEnd) {
    for (const [site, others] of across) {
      const names = [...others].map((id) => short(byId.get(id), id));
      edges.push({
        source: cloudEnd,
        target: placedIn.get(site)!,
        kind: "site-link",
        label: names.length > 2 ? `${names.slice(0, 2).join(", ")} +${names.length - 2}` : names.join(", "),
      });
    }
  }

  return { containers: [...arch.containers, ...containers], nodes: [...arch.nodes, ...nodes], edges: [...arch.edges, ...edges] };
}
